import { useMemo } from 'react';
import {
  useInfiniteQuery,
  type InfiniteData,
  type QueryKey,
  type UseInfiniteQueryResult,
} from '@tanstack/react-query';
import type { ApiResponse, PaginationMeta } from '@/types/api';

export const INFINITE_PAGE_SIZE = 20;

export interface InfiniteListResult<T>
  extends Omit<UseInfiniteQueryResult<InfiniteData<ApiResponse<T[]>>, Error>, 'data'> {
  items: T[];
  data: ApiResponse<T[]> | undefined;
  hasMore: boolean;
  loadMore: () => void;
}

export function useInfiniteList<T>(options: {
  queryKey: QueryKey;
  queryFn: (page: number) => Promise<ApiResponse<T[]>>;
  enabled?: boolean;
  pageSize?: number;
  retry?: boolean | number;
}): InfiniteListResult<T> {
  const pageSize = options.pageSize ?? INFINITE_PAGE_SIZE;
  const query = useInfiniteQuery({
    queryKey: options.queryKey,
    enabled: options.enabled,
    retry: options.retry,
    initialPageParam: 1,
    queryFn: ({ pageParam }) => options.queryFn(Number(pageParam) || 1),
    getNextPageParam: (lastPage, _pages, lastPageParam) => {
      const meta = lastPage.meta as PaginationMeta | undefined;
      if (meta && typeof meta.page === 'number' && typeof meta.totalPages === 'number') {
        return meta.page < meta.totalPages ? meta.page + 1 : undefined;
      }
      const count = Array.isArray(lastPage.data) ? lastPage.data.length : 0;
      const limit = meta?.limit ?? pageSize;
      if (!count || count < limit) return undefined;
      return (typeof lastPageParam === 'number' ? lastPageParam : 1) + 1;
    },
  });

  const items = useMemo(
    () => query.data?.pages.flatMap((page) => (Array.isArray(page.data) ? page.data : [])) ?? [],
    [query.data],
  );

  const loadMore = () => {
    if (query.hasNextPage && !query.isFetchingNextPage) {
      void query.fetchNextPage();
    }
  };

  const lastMeta = query.data?.pages[query.data.pages.length - 1]?.meta;
  const data: ApiResponse<T[]> | undefined = query.data
    ? {
        success: true,
        message: query.data.pages[0]?.message || '',
        data: items,
        meta: lastMeta,
      }
    : undefined;

  return {
    ...query,
    items,
    data,
    hasMore: Boolean(query.hasNextPage),
    loadMore,
  };
}

export function infiniteTableProps<T>(query: InfiniteListResult<T>) {
  return {
    rows: query.items,
    loading: query.isLoading,
    hasMore: query.hasMore,
    onLoadMore: query.loadMore,
    loadingMore: query.isFetchingNextPage,
  };
}
